import produce from 'immer';
import isEqual from 'react-fast-compare';
import _ from 'underscore';
import {v4 as uuidv4} from 'uuid';

import * as analytics from 'analytics/Analytics';
import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import {RegistryFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import * as RegistryConstants from 'constants/RegistryConstants';
import {RegistryLabelingStrategy, SequenceSchemaType, TagSchema} from 'constants/RegistryConstants';
import {BatchSchema} from 'constants/SampleConstants';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import getNoteLinkedObjectsOfTypes from 'notebook/notes/util/getNoteLinkedObjectsOfTypes';
import {
  isAlwaysLocked,
  isBioentityFieldColumn,
} from 'notebook/registration-tables/RegistrationTableColumnDefinitions';
import {
  FromServerRegistrationTable,
  FromServerRegistrationTableConfig,
  RegistrationTable,
  RegistrationTableBase,
  RegistrationTableConfig,
  RegistrationTableType,
  ToServerRegistrationTable,
  ToServerRegistrationTableConfig,
} from 'notebook/registration-tables/RegistrationTableConstants';
import {
  BIOENTITY_NAME_COLUMN_NAME,
  BioentityNameColumnDefinition,
  ColumnDefinition,
  EXISTING_BIOENTITY_COLUMN_NAME,
  getColumnDefinitionIndex,
  getColumnLabel,
  renderColumnLabel,
} from 'notebook/structured-tables/columns/ColumnDefinitions';
import isStructuredTableColumnReadOnly from 'notebook/structured-tables/columns/isReadOnly';
import toColumnDefinition from 'notebook/structured-tables/columns/toColumnDefinition';
import {toSchemaFieldColumnDefinition} from 'notebook/structured-tables/columns/toSchemaFieldColumnDefinition';
import {
  LegacyStructuredTableRow,
  StructuredTableColumn,
} from 'notebook/structured-tables/StructuredTableConstants';
import getBlankRow from 'notebook/structured-tables/util/getBlankRow';
import prepareCellForTableEditable from 'notebook/structured-tables/util/prepareCellForTableEditable';
import {
  getSchemaFieldColumnIds,
  makeSchemaFieldStructuredColumn,
  makeStructuredTableColumn,
} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {prepareLegacyColumnForSave} from 'notebook/structured-tables/util/StructuredTableStoreTransforms';
import {updateStructuredTableMetadataOnTable} from 'notebook/structured-tables/util/updateStructuredTableMetadata';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {CustomEvalResult} from 'notebook/tables/constants/CustomEvalConstants';
import {RowData} from 'notebook/tables/constants/RowConstants';
import {
  ColumnConstraint,
  InsertableColumnInfo,
  TableColumnType,
} from 'notebook/tables/constants/TableColumnConstants';
import {RegistrationTableMetadata, Table, TableData} from 'notebook/tables/constants/TableConstants';
import {BaseCellErrors} from 'notebook/tables/errors/TableErrorConstants';
import Excess from 'notebook/tables/excess';
import {TableOpFunction, TableOpOptions} from 'notebook/tables/excess/ops/TableOpConstants';
import * as ExcessUtil from 'notebook/tables/excess/Util';
import {CustomResolutionQueryType} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import TableUtil from 'notebook/tables/TableUtil';
import {getBioentityInfoFromTypedValue, isTypedValueEmpty} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  makeTypedValueFromCustomResolvedValue,
  ResolveQueryFn,
} from 'notebook/tables/typed-values/TypedValueResolution';
import {Note, NoteLinkedObjectType} from 'notebook/util/NoteUtilConstants';
import isBatchSchemaDeactivated from 'samples/batches/isBatchSchemaDeactivated';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import APIUtil from 'util/APIUtil';
import ErrorSubclass from 'util/ErrorSubclass';
import uniqueId from 'util/uniqueId';
import {StageRun} from 'workflows/WorkflowConstants';

/**
 * This file contains:
 *    - Some operations that are only used by the "legacy" registration table, which is the implementation
 *      that does not use the structured tables framework, and is only used for stage runs and batches.
 *    - Some operations that are shared between legacy and modern registration tables.
 */

// Class for errors detected on the client when registering
export class RegistrationException extends ErrorSubclass {}

/**
 * @param fileId API ID of the file
 * @param isConfig Boolean indicating if this file is a template/config or executable
 * @returns Returns a boolean indicating whether the registration table is supported for the given file
 */
export function isRegistrationTableSupportedForFile(fileId: string, isConfig: boolean): boolean {
  return (
    APIUtil.isStageEntry(fileId) ||
    (isFeatureEnabled({feature: RegistryFeature.REGISTRY_CREATE}) &&
      (APIUtil.isEntry(fileId) || (APIUtil.isDocument(fileId) && !isConfig)))
  );
}

/**
 * @param fileId API ID of the file
 * @param isConfig Boolean indicating if this file is a template/config or executable
 * @returns Returns a boolean indicating whether the config registration table is supported for the given file
 */
export function isRegistrationTableConfigSupportedForFile(fileId: string, isConfig: boolean): boolean {
  if (APIUtil.isSubtemplate(fileId) && !BenchlingConfig.ELN_ENABLE_INSERT_NEW_REG_TABLE) {
    // Don't allow inserts of legacy registration table into sub-templates
    return false;
  }
  return (
    APIUtil.isStageEntryTemplate(fileId) ||
    (isFeatureEnabled({feature: RegistryFeature.REGISTRY_CREATE}) &&
      (APIUtil.isEntryTemplate(fileId) ||
        APIUtil.isSubtemplate(fileId) ||
        (APIUtil.isDocument(fileId) && isConfig)))
  );
}

export function createLegacyRegistrationTableConfig(
  name: string,
  tagSchema: TagSchema,
  tableType: RegistrationTableType,
  labelingStrategy: RegistryLabelingStrategy,
  fileId: string,
  selectorsById: {[selectorId: string]: SchemaFieldSelector}
): RegistrationTableConfig {
  analytics.deprecatedTrackEvent(ELN_ANALYTICS_ACTIONS.CREATED_REGISTRATION_TABLE_CONFIG);
  return {
    cid: uniqueId('regtcfg'),
    type: NoteLinkedObjectType.REGISTRATION_TABLE_CONFIG,
    columns: getLegacyDefaultStructuredTableColumns({
      fileId,
      tableType,
      labelingStrategy,
      tagSchema,
      batchSchema: null,
      selectorsById,
    }),
    rows: [],
    name,
    tagSchemaId: tagSchema.id,
    tagSchema,
    batchSchema: null,
    tableType,
    labelingStrategy,
    provenanceUuid: uuidv4(),
  };
}

// TODO(saif): Pull into util module and share with NoteUtil
function _addCid(item: {id: string | number; cid?: string | null}, prefix: string, cidIdMap: CidIdMap): void {
  assert(item.cid == null, 'Item already has cid');
  item.cid = uniqueId(prefix);
  cidIdMap[item.cid] = item.id;
  // @ts-expect-error: id is required in the parameter, but removed for output
  delete item.id;
}

export function prepareForStore(registrationTable: FromServerRegistrationTable, cidIdMap: CidIdMap): void {
  _addCid(registrationTable, 'regt', cidIdMap);
  for (const column of registrationTable.columns) {
    _addCid(column, 'dtcol', cidIdMap);
  }
}

export function prepareConfigForStore(
  registrationTableConfig: FromServerRegistrationTableConfig,
  cidIdMap: CidIdMap
): void {
  _addCid(registrationTableConfig, 'regtcfg', cidIdMap);
  for (const column of registrationTableConfig.columns) {
    _addCid(column, 'dtcol', cidIdMap);
  }
}

export function prepareForSave(
  registrationTable: RegistrationTable,
  cidIdMap: CidIdMap
): ToServerRegistrationTable {
  return {
    ...registrationTable,
    id: cidIdMap[registrationTable.cid] as string,
    columns: registrationTable.columns.map((column) => prepareLegacyColumnForSave(column, cidIdMap)),
  };
}

/**
 * These are some of the columns used in a Registration Table. For the rest, see
 * StructuredTableUtilsForRegistration.ts. Agent keep in sync: benchling/notebook/structured_tables/constants/registration_tables.py
 */
export const STAGE_RUN_COLUMN_NAME = 'Stage Run';
export const BASES_COLUMN_NAME = 'Bases (Natural bases)';
export const BASES_HELM_COLUMN_NAME = 'Bases (HELM)';
export const BASES_IDT_COLUMN_NAME = 'Bases (IDT)';
export const SMILES_COLUMN_NAME = 'SMILES';

export function prepareConfigForSave(
  registrationTableConfig: RegistrationTableConfig,
  cidIdMap: CidIdMap
): ToServerRegistrationTableConfig {
  return {
    ...registrationTableConfig,
    id: cidIdMap[registrationTableConfig.cid] as string,
    columns: registrationTableConfig.columns.map((column) => prepareLegacyColumnForSave(column, cidIdMap)),
  };
}

export function getRegistrationTablesInNote(note: Note): Array<RegistrationTable> {
  return getNoteLinkedObjectsOfTypes(note, [
    ModelTypeConstants.REGISTRATION_TABLE,
  ]) as Array<RegistrationTable>;
}

export function getRegistrationTableConfigsInNote(note: Note): Array<RegistrationTableConfig> {
  return getNoteLinkedObjectsOfTypes(note, [
    ModelTypeConstants.REGISTRATION_TABLE_CONFIG,
  ]) as Array<RegistrationTableConfig>;
}

interface ExistingBioentityCustomEvalOptions {
  tagSchema: TagSchema;
}

function existingBioentityCustomEval(
  cellTypedValue: TypedValue,
  columnDefinition: ColumnDefinition,
  [existingBioentityTypedValue]: [TypedValue],
  {tagSchema}: ExistingBioentityCustomEvalOptions,
  resolveFromResolutionQuery: ResolveQueryFn
): CustomEvalResult {
  if (
    existingBioentityTypedValue == null ||
    existingBioentityTypedValue.type !== BaseTypedValueType.BIOENTITY
  ) {
    return resolveTypedValueResult({type: BaseTypedValueType.STRING, rawValue: ''}, cellTypedValue, null);
  }

  const {resolvedValue, error} = resolveFromResolutionQuery(
    cellTypedValue,
    {
      type: ResolutionQueryType.CUSTOM,
      customResolutionQueryType: CustomResolutionQueryType.LEGACY_REGISTRATION_TABLE_EXISTING_BIOENTITY,
      bioentityId: existingBioentityTypedValue.bioentitySummary.id,
      columnDefinition,
      tagSchema,
    },
    makeTypedValueFromCustomResolvedValue
  );

  return resolveTypedValueResult(resolvedValue, cellTypedValue, error);
}

function resolveTypedValueResult(
  liveValue: TypedValue,
  cellValue: TypedValue,
  error: BaseCellErrors | null
): CustomEvalResult {
  if (BenchlingConfig.REGISTRATION_TABLE_ENABLE_LIVE_TAGS) {
    return {val: liveValue, error: null, locked: true};
  }

  if (isTypedValueEmpty(cellValue) && isTypedValueEmpty(liveValue)) {
    return {val: cellValue, error: null, locked: true};
  }

  // Don't mask more serious errors
  if (!isEqual(cellValue, liveValue) && error == null) {
    error = new ExcessUtil.Warning(
      'Stale value detected - press the refresh button in the upper right corner of this table to update.'
    );
  }
  return {val: cellValue, error, locked: true};
}

/**
 * Clear and lock a row's schema tag cells if it has an existing bioentity.
 */
function setFieldsForExistingBioentity({
  columnDefinitions,
  tableRow,
  rowIndex,
  tagSchema,
}: {
  columnDefinitions: Array<ColumnDefinition>;
  tableRow: Array<Cell>;
  rowIndex: number;
  tagSchema: TagSchema;
}): Array<Cell> {
  const bioentityIdx = getColumnDefinitionIndex({
    columnDefinitionType: TableColumnType.EXISTING_BIOENTITY,
    tableColumnDefinitions: columnDefinitions,
  });
  const bioentityCell = tableRow[bioentityIdx];
  const hasExistingBioentity =
    bioentityCell.text !== '' ||
    (bioentityCell.typedValue && Boolean(getBioentityInfoFromTypedValue(bioentityCell.typedValue)));
  return columnDefinitions.map((columnDefinition, j) => {
    const isBioentityNameColumn = columnDefinition.type === TableColumnType.BIOENTITY_NAME;
    const isBasesColumn = columnDefinition.type === TableColumnType.DNA_BASES;
    const isBioentitySchemaFieldColumn =
      columnDefinition.type === TableColumnType.TAG_VALUE &&
      isBioentityFieldColumn(columnDefinition, tagSchema);
    if (
      !hasExistingBioentity ||
      (!isBioentityNameColumn && !isBioentitySchemaFieldColumn && !isBasesColumn)
    ) {
      return {
        ...tableRow[j],
        locked: isAlwaysLocked(columnDefinition),
      };
    }
    return {
      ...tableRow[j],
      customEvalArgs: [`=${ExcessUtil.toSpreadsheetIndex({row: rowIndex, column: bioentityIdx})}`],
      customEvalOptions: {tagSchema},
      customEval: existingBioentityCustomEval,
    } as Cell<ExistingBioentityCustomEvalOptions>;
  });
}

function transformRowsForTableEditable(
  rows: Array<LegacyStructuredTableRow>,
  columns: Array<StructuredTableColumn>,
  columnDefinitions: Array<ColumnDefinition>,
  startIndex: number,
  tagSchema: TagSchema | null
): Array<Array<Cell>> {
  return rows.map((row, i) => {
    const tableRow = columnDefinitions.map((columnDefinition, j) => {
      let cellValue = row.cells[j] ? prepareCellForTableEditable(row.cells[j], columns, i, j) : {text: ''};
      if (row.isFinalized || isAlwaysLocked(columnDefinition)) {
        cellValue = {...cellValue, locked: true};
      }
      return cellValue;
    });
    if (row.isFinalized || !tagSchema) {
      // The row is already finalized and contains all the required information
      return tableRow;
    }
    return setFieldsForExistingBioentity({
      columnDefinitions,
      tableRow,
      rowIndex: startIndex + i,
      tagSchema,
    });
  });
}

export function getTableRows(rows: Array<LegacyStructuredTableRow>): Array<Array<Cell>> {
  return rows.map((row) => row.cells);
}

export function getRowWithDefaults(
  row: Array<Cell>,
  rowWithDefaults: Array<Cell>,
  columnDefinitions: Array<ColumnDefinition>
): Array<Cell> {
  const newRow = [];
  for (let j = 0; j < rowWithDefaults.length; j++) {
    const cell = row[j];
    const columnDefinition = columnDefinitions[j];
    // Autogenerated empty cells and locked cells can be replaced with defaults;
    // otherwise keep the cell, since it might have been explicitly pasted.
    if (TableUtil.isCellEmpty(cell) || isAlwaysLocked(columnDefinition)) {
      newRow[j] = rowWithDefaults[j];
    } else {
      newRow[j] = cell;
    }
  }
  return newRow;
}

export function getRegistrationTableRows(
  tableRows: Array<Array<Cell>>,
  rowData: Array<RowData>
): Array<LegacyStructuredTableRow> {
  assert(
    tableRows.length === rowData.length,
    `Invalid rowData length. Expected ${tableRows.length}, got ${rowData.length}`
  );
  return tableRows.map((cells, i) => ({isFinalized: rowData[i].isFinalized as boolean, cells}));
}

export function getDefaultRows(
  fileId: string,
  structuredTableColumns: Array<StructuredTableColumn>,
  stageRuns: Array<StageRun>
): Array<Array<Cell>> {
  // it is possible for a stage entry to have no stage runs, in which case we just render a default row
  if (APIUtil.apiIdToType(fileId) !== ModelTypeConstants.STAGE_ENTRY || stageRuns.length === 0) {
    return [getBlankRow(0, structuredTableColumns, null)];
  }
  return stageRuns.map(({name}, i) => getBlankRow(i, structuredTableColumns, name));
}

function _requiredSchemas(
  tableType: RegistrationTableType,
  tagSchema: TagSchema | null,
  batchSchema: BatchSchema | null
): Array<TagSchema | BatchSchema | null> {
  const requiredSchemas: Array<TagSchema | BatchSchema | null> = [tagSchema];
  if (tableType === RegistrationTableType.BATCH) {
    requiredSchemas.push(batchSchema);
  }
  return requiredSchemas;
}

export function areRequiredSchemasArchived(
  tableType: RegistrationTableType,
  tagSchema: TagSchema | null,
  batchSchema: BatchSchema | null
): boolean {
  return _requiredSchemas(tableType, tagSchema, batchSchema).some(
    (schema) => schema != null && isArchived(schema)
  );
}

export function isRequiredBatchSchemaDeactivated(
  tableType: RegistrationTableType,
  batchSchema: BatchSchema | null
): boolean {
  return tableType === RegistrationTableType.BATCH && isBatchSchemaDeactivated(batchSchema);
}

export function convertRegistrationTableColumnsAndRowsToTableProps({
  registrationTable,
  stageRuns,
}: {
  registrationTable: RegistrationTableBase;
  stageRuns: Array<StageRun>;
}): TableData {
  const {name, columns, rows, tagSchema, batchSchema, labelingStrategy} = registrationTable;

  const columnDefinitions: Array<ColumnDefinition> = columns.map((structuredTableColumn) =>
    structuredTableColumnToColumnDefinition(structuredTableColumn, tagSchema!, batchSchema, stageRuns)
  );
  const columnLabels: Array<string> = columnDefinitions.map((columnDefinition) =>
    getColumnLabel(columnDefinition)
  );
  // fill any missing cells in rows with blank strings
  const table: Array<Array<Cell>> = transformRowsForTableEditable(
    rows,
    columns,
    columnDefinitions,
    0,
    tagSchema
  );

  const rowData: Array<RowData> = rows.map((row) => ({isFinalized: row.isFinalized}));
  const columnConstraints: Array<ColumnConstraint> = columnDefinitions.map((columnDefinition, i) => ({
    editLabelDisabled: true,
    structuredTableColumn: columns[i],
    columnDefinition,
    renderedLabel: BenchlingConfig.ELN_ENABLE_COLUMN_TYPE_ICONS
      ? getColumnLabel(columnDefinition)
      : renderColumnLabel(columnDefinition),
  }));
  const widths = columns.map(({width}) => width);
  return {
    name,
    widths,
    columnConstraints,
    columnLabels,
    table,
    rowData,
    ...(labelingStrategy ? {metadata: {labelingStrategy}} : {}),
  };
}

export function createTable({
  registrationTable,
  stageRuns,
}: {
  registrationTable: RegistrationTable | RegistrationTableConfig;
  stageRuns: Array<StageRun>;
}): Table {
  const {name, columnConstraints, columnLabels, table, rowData} =
    convertRegistrationTableColumnsAndRowsToTableProps({registrationTable, stageRuns});

  return {
    name,
    headerRow: false,
    headerCol: false,
    columnLabels,
    lockedDims: true,
    allowAddRows: true,
    allowRowRemoval: true,
    allowNoRows: false,
    allowColumnEdits: true,
    disallowCustomColumns: true,
    legacyFirstEditableColumnIndex: getFirstEditableColumnIndex(registrationTable.columns),
    widths: registrationTable.columns.map(({width}) => width),
    table,
    rowData,
    columnConstraints,
    defaultAffixedColumnCount: 1,
    _tableType: registrationTable.type,
    provenanceUuid: registrationTable.provenanceUuid,
  };
}

export function getFirstEditableColumnIndex(structuredTableColumns: Array<StructuredTableColumn>): number {
  const firstEditableColumnIndex = structuredTableColumns.findIndex(
    (column) => column.columnType === TableColumnType.TAG_VALUE
  );
  if (firstEditableColumnIndex === -1) {
    return structuredTableColumns.length;
  } else {
    return firstEditableColumnIndex;
  }
}

export function getInsertableColumnInfos(
  tagSchema: TagSchema | null,
  batchSchema: BatchSchema | null,
  structuredTableColumns: Array<StructuredTableColumn>,
  selectorsById: {[selectorId: string]: SchemaFieldSelector}
): Array<InsertableColumnInfo> {
  if (!tagSchema) {
    return [];
  }
  const fields = [...tagSchema.fields, ...(batchSchema ? batchSchema.fields : [])];
  const schemaFieldColumnIds = getSchemaFieldColumnIds(structuredTableColumns);
  const unusedSchemaFields = fields.filter((field) => !schemaFieldColumnIds.includes(field.id));
  return unusedSchemaFields.map((field) => {
    const structuredTableColumn = makeSchemaFieldStructuredColumn(field, selectorsById);
    return {
      columnLabels: field.displayName,
      columnConstraints: {
        columnDefinition: toSchemaFieldColumnDefinition(field, structuredTableColumn),
        structuredTableColumn,
      },
    };
  });
}

/**
 * Get the default structured table columns for a legacy registration table (i.e. the reg table implementation
 * that does not use the structured tables framework).
 *
 * Agent keep in sync: get_default_column_specs_for_registration_table
 * in benchling/workflows/lib/registration_tables.py
 */
export function getLegacyDefaultStructuredTableColumns({
  fileId,
  tableType,
  labelingStrategy,
  tagSchema,
  batchSchema,
  selectorsById,
}: {
  fileId: string;
  tableType: RegistrationTableType;
  labelingStrategy: RegistryLabelingStrategy | null;
  tagSchema: TagSchema | null;
  batchSchema: BatchSchema | null;
  selectorsById: {[selectorId: string]: SchemaFieldSelector};
}): Array<StructuredTableColumn> {
  const stageRunColumns = APIUtil.isNotebookEntryOrTemplate(fileId)
    ? []
    : [makeStructuredTableColumn({columnType: TableColumnType.STAGE_RUN, name: STAGE_RUN_COLUMN_NAME})];
  const bioentityColumns = tagSchema
    ? _.compact([
        makeStructuredTableColumn({
          columnType: TableColumnType.EXISTING_BIOENTITY,
          name: EXISTING_BIOENTITY_COLUMN_NAME,
        }),
        labelingStrategy &&
          RegistryConstants.LABELING_STRATEGIES_REQUIRING_NAME.includes(labelingStrategy) &&
          makeStructuredTableColumn({
            columnType: TableColumnType.BIOENTITY_NAME,
            name: BIOENTITY_NAME_COLUMN_NAME,
            isRequired: true,
          }),
        tagSchema.sequenceType === SequenceSchemaType.OLIGO &&
          makeStructuredTableColumn({columnType: TableColumnType.DNA_BASES, name: 'Bases'}),
      ])
    : [];
  const fileTagColumns = tagSchema
    ? tagSchema.fields.map((schemaField: SchemaField) =>
        makeSchemaFieldStructuredColumn(schemaField, selectorsById)
      )
    : [];
  const batchTagColumns = batchSchema
    ? batchSchema.fields.map((schemaField: SchemaField) =>
        makeSchemaFieldStructuredColumn(schemaField, selectorsById)
      )
    : [];

  if (tableType === RegistrationTableType.BATCH) {
    return [
      makeStructuredTableColumn({columnType: TableColumnType.BATCH, name: 'Batch'}),
      ...stageRunColumns,
      ...bioentityColumns,
      ...fileTagColumns,
      ...batchTagColumns,
    ];
  } else {
    // Handle FILE RegistrationTableType
    return [...bioentityColumns, ...stageRunColumns, ...fileTagColumns];
  }
}

export function structuredTableColumnToColumnDefinition(
  structuredTableColumn: StructuredTableColumn,
  tagSchema: TagSchema,
  batchSchema: BatchSchema | null,
  stageRuns: Array<StageRun>
): ColumnDefinition {
  if (structuredTableColumn.columnType === TableColumnType.STRING) {
    // Schema field column has been deleted. This could happen when we backfilled Reg table columns if Batch
    // schema field has been deleted
    const {name, isReadOnly, columnGroup, isArchived, isRequired} = structuredTableColumn;
    return {
      type: TableColumnType.MISSING_FIELD,
      structuredTableColumnName: name,
      locked: isReadOnly,
      columnGroup,
      isRequired,
      isArchived,
    };
  }

  return toColumnDefinition({
    structuredTableColumn,
    stageRuns,
    schemas: _.compact([tagSchema, batchSchema]),
  });
}

export function getColumnDefinitionsFromStructuredTableColumns(
  structuredTableColumns: Array<StructuredTableColumn>,
  {
    tagSchema,
    batchSchema,
    stageRuns,
  }: {tagSchema: TagSchema; batchSchema: BatchSchema | null; stageRuns: Array<StageRun>}
): Array<ColumnDefinition> {
  return structuredTableColumns.map((structuredTableColumn) =>
    structuredTableColumnToColumnDefinition(structuredTableColumn, tagSchema, batchSchema, stageRuns)
  );
}

/**
 * The following interfaces are descriptions of table ops that need to
 * be performed if the labeling startegy is not consistent with the
 * table.
 */
export interface RemoveColumnTableOp {
  start: number;
  end: number;
  type: 'remove_column';
}

export interface AddColumnTableOp {
  start: number;
  end: number;
  insertableColumnInfo: InsertableColumnInfo;
  type: 'add_column';
}

export interface MakeColumnEditableAndRequiredTableOp {
  index: number;
  type: 'make_column_editable_and_required';
}

export interface NopTableOp {
  type: 'nop';
}

export type LabelingStrategyTableOp =
  | RemoveColumnTableOp
  | AddColumnTableOp
  | MakeColumnEditableAndRequiredTableOp
  | NopTableOp;

/**
 * Determine if we need to modify the Table provided to be consistent
 * with LabelingStrategy provided.  This could be mean removing a
 * column, adding a column, making an existing column editable and required,
 * or not doing anything.  The operation to be performed will be described
 * by the LabelingStrategyTableOp returned.
 *
 * If the table contains finalized data, then an assertion error will
 * be thrown.
 *
 * @param table the table that we want to potentially update
 * @param labelingStrategy our new labeling strategy for the table
 */
export function getTableOpForLabelingStrategyChange({
  table,
  labelingStrategy,
}: {
  table: TableData;
  labelingStrategy: RegistryLabelingStrategy;
}): LabelingStrategyTableOp {
  const bioentityNameColumnIndex = getColumnDefinitionIndex({
    columnDefinitionType: TableColumnType.BIOENTITY_NAME,
    tableColumnDefinitions: table.columnConstraints.map((columnConstraint) =>
      columnConstraint && columnConstraint.columnDefinition ? columnConstraint.columnDefinition : null
    ),
    isOptional: true,
  });
  const requiresName = RegistryConstants.LABELING_STRATEGIES_REQUIRING_NAME.includes(labelingStrategy);
  const needsToAddBioentityName = requiresName && bioentityNameColumnIndex < 0;
  const needsToRemoveBioentityName = !requiresName && bioentityNameColumnIndex > -1;
  const needsToMakeBioentityNameEditableAndRequired =
    requiresName &&
    bioentityNameColumnIndex > -1 &&
    !isEditableAndRequiredColumn(table.columnConstraints[bioentityNameColumnIndex]);

  if (
    !(needsToAddBioentityName || needsToRemoveBioentityName || needsToMakeBioentityNameEditableAndRequired)
  ) {
    return {type: 'nop'};
  }

  assert(
    !(table && table.rowData && table.rowData.some((row) => Boolean(row.isFinalized))),
    'Attempted to modify the labeling strategy of a table with finalized data'
  );

  if (needsToAddBioentityName) {
    return createAddBioentityNameColumnTableOp(table);
  } else if (needsToRemoveBioentityName) {
    return createRemoveBioentityNameColumnTableOp(table);
  } else {
    return {type: 'make_column_editable_and_required', index: bioentityNameColumnIndex};
  }
}

function isEditableAndRequiredColumn(columnConstraint: ColumnConstraint | null | undefined): boolean {
  const {columnDefinition, structuredTableColumn} = columnConstraint ?? {};
  if (!columnDefinition?.isRequired) {
    return false;
  }
  if (structuredTableColumn == null) {
    return !columnDefinition.locked;
  }
  return structuredTableColumn.isRequired && !structuredTableColumn.isReadOnly;
}

function withColumnEditableAndRequired(table: Table, index: number): Table {
  return produce(table, (draftTable) => {
    const columnConstraint = draftTable.columnConstraints[index];
    if (columnConstraint == null) {
      return;
    }
    const {columnDefinition, structuredTableColumn} = columnConstraint;
    if (structuredTableColumn != null) {
      structuredTableColumn.isRequired = true;
      structuredTableColumn.isReadOnly = false;
    }
    if (columnDefinition != null) {
      columnDefinition.isRequired = true;
      columnDefinition.locked =
        structuredTableColumn != null ? isStructuredTableColumnReadOnly(structuredTableColumn) : false;
    }
  });
}

function createAddBioentityNameColumnTableOp(table: TableData): AddColumnTableOp {
  // insert the bioentity name column after the existing bioentity column. Match on the structured column
  // type where available: reference columns (e.g. an ADC's Protein) also have EXISTING_BIOENTITY column
  // definitions but are not the registered entity.
  const bioentityColumnIndex = getColumnDefinitionIndex({
    columnDefinitionType: TableColumnType.EXISTING_BIOENTITY,
    tableColumnDefinitions: table.columnConstraints.map((columnConstraint) => {
      if (columnConstraint?.structuredTableColumn != null) {
        const {columnType, columnGroup} = columnConstraint.structuredTableColumn;
        return {type: columnType, columnGroup};
      }
      return columnConstraint?.columnDefinition ?? null;
    }),
  });
  // Only added for labeling strategies that require a name, so the column is always required.
  const structuredTableColumn = makeStructuredTableColumn({
    columnType: TableColumnType.BIOENTITY_NAME,
    name: BIOENTITY_NAME_COLUMN_NAME,
    isRequired: true,
  });
  const columnDefinition: BioentityNameColumnDefinition = {
    type: TableColumnType.BIOENTITY_NAME,
    isRequired: true,
  };
  const insertableColumnInfo = {
    columnLabels: getColumnLabel(columnDefinition),
    columnConstraints: {
      columnDefinition,
      structuredTableColumn,
    },
  };

  return {
    type: 'add_column',
    start: bioentityColumnIndex,
    end: bioentityColumnIndex,
    insertableColumnInfo,
  };
}

function isColumnConstraintForStructuredTable(columnConstraint: ColumnConstraint | null): boolean {
  return columnConstraint != null && columnConstraint.columnDefinition != null;
}

function createRemoveBioentityNameColumnTableOp(table: TableData): RemoveColumnTableOp {
  const bioentityNameColumnIndex = getColumnDefinitionIndex({
    columnDefinitionType: TableColumnType.BIOENTITY_NAME,
    tableColumnDefinitions: table.columnConstraints.map((columnConstraint) =>
      isColumnConstraintForStructuredTable(columnConstraint) ? columnConstraint!.columnDefinition : null
    ),
  });

  return {
    type: 'remove_column',
    start: bioentityNameColumnIndex,
    end: bioentityNameColumnIndex,
  };
}

/**
 * A utility function to update the RegistrationTableMetadata on a FullTableMap, given
 * a targetTableName to make the modification on.
 *
 * @param table the table to update
 * @param metadata the RegistrationTableMetadata to update the table with
 */
export function updateMetadataOnTable(metadata: RegistrationTableMetadata, table: Table): Table {
  return produce(table, (draftTable) => {
    draftTable.metadata = {
      ...draftTable.metadata,
      ...metadata,
    };
  });
}

/**
 * This is a utility method that's shared among all the registration table components:
 * RegistrationTable and RegistrationTableConfig, as well the "new registration table"
 * implemented by RegistrationStructuredTableAdapter.
 *
 * When we change the labeling strategy for a registration table, we may
 * meed to modify the table's columns to fit with the new
 * strategy. Since the table could be used in cross-table formulas, we need to
 * use the "table op" system to modify the table's columns. The table op system
 * takes care of modifying the table and also updates any formulas that depend on the modified columns.
 */
export function performColumnOpAndSetLabelingStrategy({
  table,
  performTableOpOnTable,
  labelingStrategy,
  isStructuredTable,
}: {
  table: TableData;
  performTableOpOnTable: ({op, options}: {op: TableOpFunction; options?: TableOpOptions}) => void;
  labelingStrategy: RegistryLabelingStrategy;
  isStructuredTable?: boolean;
}): void {
  const opToPerform = getTableOpForLabelingStrategyChange({table, labelingStrategy});
  let tableColumnsOp: TableOpFunction;

  switch (opToPerform.type) {
    case 'nop': {
      tableColumnsOp = (table) => ({
        table,
        tokenUpdaters: [],
        dim: null,
        box: null,
      });
      break;
    }
    case 'add_column': {
      const {start, end, insertableColumnInfo} = opToPerform;
      tableColumnsOp = (table, documentName) =>
        Excess.ops.insertColumnsRight(table, documentName, start, end, insertableColumnInfo);
      break;
    }
    case 'remove_column': {
      const {start, end} = opToPerform;
      tableColumnsOp = (table, documentName) => Excess.ops.deleteColumns(table, documentName, start, end);
      break;
    }
    case 'make_column_editable_and_required': {
      const {index} = opToPerform;
      tableColumnsOp = (table) => ({
        table: withColumnEditableAndRequired(table, index),
        tokenUpdaters: [],
        dim: null,
        box: null,
      });
      break;
    }
    default:
      assert(false, 'Unknown type of op to perform', opToPerform);
  }

  performTableOpOnTable({
    op: (originalTable, documentName, evaluatedTable, tableMap) => {
      const {table, tokenUpdaters, box, dim} = tableColumnsOp(
        originalTable,
        documentName,
        evaluatedTable,
        tableMap
      );

      let updatedTable: Table;
      if (isStructuredTable) {
        updatedTable = updateStructuredTableMetadataOnTable({labelingStrategy}, table);
      } else {
        updatedTable = updateMetadataOnTable({labelingStrategy}, table);
      }
      return {table: updatedTable, tokenUpdaters, box, dim};
    },
    options: {ignoreLockedDimensions: true},
  });
}
